import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ExportButton } from '@/components/common/ExportButton';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { searchEntities } from '@/services/anatel';

const PAGE_SIZE = 50;

export function AnatelEntitiesPage() {
  const { t, i18n } = useTranslation(['anatel', 'common']);
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['anatel', 'entities', query, offset],
    queryFn: () => searchEntities({ query: query || null, limit: PAGE_SIZE, offset }),
    placeholderData: keepPreviousData,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('anatel:entities.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('anatel:entities.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="anatel:entities.explainer" />

      <FieldGuide i18nKey="anatel:entities.guide" level={2} />

      {data?.as_of ? <AsOfStrip asOf={data.as_of} /> : null}

      <div className="flex justify-end">
        <ExportButton path="/api/v1/anatel/export/entities" body={{ query }} />
      </div>

      <label className="block max-w-md space-y-1">
        <span className="text-xs font-medium text-foreground">
          {t('anatel:entities.search')}
        </span>
        <input
          type="search"
          value={query}
          placeholder={t('anatel:entities.searchPlaceholder')}
          onChange={(event) => {
            setQuery(event.target.value);
            setOffset(0);
          }}
          className="w-full rounded-md border bg-background px-3 py-2 text-sm text-foreground"
        />
      </label>

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : data.items.length === 0 ? (
        <EmptyState hint={t('anatel:entities.emptyHint')} />
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground" aria-live="polite">
            {t('anatel:decisions.showing', {
              from: number.format(offset + 1),
              to: number.format(offset + data.items.length),
              total: number.format(data.total),
            })}
          </p>

          <div className="overflow-x-auto rounded-lg border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">{t('anatel:entities.columns.company')}</th>
                  <th className="px-3 py-2 font-medium">{t('anatel:entities.columns.cnpj')}</th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('anatel:entities.columns.decisions')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('anatel:entities.columns.fined')}
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    {t('anatel:entities.columns.totalFined')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.cnpj} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <Link
                        to={`/anatel/e/${item.cnpj}`}
                        className="font-medium text-primary underline-offset-4 hover:underline"
                      >
                        {item.canonical_name}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted-foreground">
                      {item.cnpj}
                      {item.cnpj_valid === false ? (
                        <span
                          className="ml-1 text-amber-600 dark:text-amber-400"
                          title={t('anatel:entities.cnpjInvalid')}
                        >
                          !
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {number.format(item.decisions)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                      {number.format(item.fined_decisions)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums text-foreground">
                      {/* Blank, never 0 -- in a money column a zero is a value. */}
                      {item.total_fined_brl === null ? '' : currency.format(item.total_fined_brl)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between">
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              {t('anatel:decisions.previous')}
            </button>
            <button
              type="button"
              disabled={offset + PAGE_SIZE >= data.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
              className="rounded-md border px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
            >
              {t('anatel:decisions.next')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
